
import {
  Component, Prop, Vue, Watch,
} from 'nuxt-property-decorator'
import { RichTextElement } from 'fsxa-api'
import { globalLabel, globalLabelAsString } from '../../shared/general/services/StoreService'
import BaseDropdown from '../base/form/BaseDropdown.vue'
import BaseIcon from '../base/BaseIcon.vue'
import GlobalLabelWrapper from '../GlobalLabelWrapper.vue'
import IOption from '../../shared/general/interfaces/IOption'
import { TTargetGroup } from '../../shared/general/types/TTargetGroup'
import { getURLParameters } from '../../shared/general/services/URLParameterService'

@Component({
  name: 'ServiceFilterPanel',
  components: {
    BaseDropdown,
    BaseIcon,
    GlobalLabelWrapper,
  },
})
export default class ServiceFilterPanel extends Vue {
  @Prop({ default: '' }) searchTerm! : string

  @Prop({ required: true }) targetGroup! : TTargetGroup

  @Prop({ default: () => [] }) serviceObjectOptions! : IOption[]

  @Prop({ default: () => [] }) serviceDivisionOptions! : IOption[]

  @Prop({ default: () => [] }) industryOptions! : IOption[]

  @Prop({ default: true }) showIndustryFilter! : boolean

  @Prop({ default: 0 }) numberOfTotalResults! : number

  @Prop({ default: false }) resetFilters! : boolean

  private selectedServiceObjectOption : IOption = { value: '', label: '', identifier: '' }

  private selectedServiceDivisionOption : IOption = { value: '', label: '', identifier: '' }

  private selectedIndustryOption : IOption = { value: '', label: '', identifier: '' }

  mounted () {
    this.checkPreFilter()

    // this serves as the single search trigger after the filter panel have been mounted
    this.$emit('trigger-search')
  }

  private get allLabel () : string {
    return globalLabelAsString('all_label')
  }

  private get filterButtonLabel () : string| RichTextElement[] {
    return globalLabel('filters_label')
  }

  private get serviceObjectLabel () : string {
    return globalLabelAsString('service_object_label')
  }

  private get serviceDivisionLabel () : string {
    return globalLabelAsString('service_division_label')
  }

  private get industryLabel () : string {
    return globalLabelAsString('industry_label')
  }

  /**
   * 1. read URL parameters
   * 2. set dropdown values
   * 3. trigger search
   */
  private checkPreFilter () : void {
    const params = getURLParameters(['service-object', 'service-division', 'industry'])
    const newOption = (value : string) => ({ value, label: value, identifier: value })
    if (params.industry) {
      this.selectIndustry(newOption(params.industry))
    }
    if (params['service-division']) {
      this.selectServiceDivision(newOption(params['service-division']))
    }
    if (params['service-object']) {
      this.selectServiceObject(newOption(params['service-object']))
    }
  }

  private get resultLabel () : string {
    switch (this.numberOfTotalResults) {
      case 0:
        return globalLabelAsString('no_search_results_found_label')
      case 1:
        return `1 ${globalLabelAsString('result_for_label')} "${this.searchTerm}"`
      default:
        return `${this.numberOfTotalResults} ${globalLabelAsString('results_for_label')} "${this.searchTerm}"`
    }
  }

  private get serviceDivisionOptionsWithAll () : IOption[] {
    return [this.allOption, ...this.sortOptions(this.serviceDivisionOptions)]
  }

  private get serviceObjectOptionsWithAll () : IOption[] {
    return [this.allOption, ...this.sortOptions(this.serviceObjectOptions)]
  }

  private get industryOptionsWithAll () : IOption[] {
    return [this.allOption, ...this.sortOptions(this.industryOptions)]
  }

  private get allOption () : IOption {
    return {
      label: this.allLabel,
      value: '__all__',
    }
  }

  private sortOptions (options : IOption[]) : IOption[] {
    const { locale } = this.$store.state.Locale
    return options.sort((first, second) => first.label.localeCompare(second.label, locale))
  }

  private optionLabel (option : IOption) : string {
    return (option === this.allOption || !option.label) ? '' : option.label
  }

  private selectServiceObject (option : IOption, triggerSearch : boolean = false) : void {
    this.selectedServiceObjectOption = option
    this.$emit('select-service-object', this.optionLabel(option))
    if (triggerSearch) this.$emit('trigger-search')
  }

  private selectServiceDivision (option : IOption, triggerSearch : boolean = false) : void {
    this.selectedServiceDivisionOption = option
    this.$emit('select-service-division', this.optionLabel(option))
    if (triggerSearch) this.$emit('trigger-search')
  }

  private selectIndustry (option : IOption, triggerSearch : boolean = false) : void {
    this.selectedIndustryOption = option
    this.$emit('select-industry', this.optionLabel(option))
    if (triggerSearch) this.$emit('trigger-search')
  }

  @Watch('resetFilters')
  private onResetFilters (reset : boolean) : void {
    if (reset) {
      this.selectIndustry(this.allOption)
      this.selectServiceObject(this.allOption)
      this.selectServiceDivision(this.allOption)
    }
  }
}
